import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { authAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Home, Loader2, Eye, EyeOff, AlertCircle } from "lucide-react";

const AdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check if redirected due to expired token
  useEffect(() => {
    if (searchParams.get("expired") === "true") {
      setSessionExpired(true);
      toast.error("Your session has expired. Please log in again.");
    }
  }, [searchParams]);

  // Redirect if already logged in (wait for auth check to complete)
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const redirectPath = sessionStorage.getItem("redirect_after_login") || "/admin";
      sessionStorage.removeItem("redirect_after_login");
      navigate(redirectPath);
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    setSessionExpired(false);
    
    try {
      const response = await authAPI.login({ username, password });
      const success = login(response.data.access_token);
      
      if (success) {
        toast.success("Welcome back!");
        const redirectPath = sessionStorage.getItem("redirect_after_login") || "/admin";
        sessionStorage.removeItem("redirect_after_login");
        navigate(redirectPath);
      } else {
        toast.error("Login failed. Please try again.");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Authentication failed";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex" data-testid="admin-login-page">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1559934303-74c6434ad39f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxoYXBweSUyMHRyYXZlbGVycyUyMGNvdXBsZSUyMHJlbGF4aW5nfGVufDB8fHx8MTc2ODM4ODU4OHww&ixlib=rb-4.1.0&q=85"
          alt="Travelers"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2
            className="text-4xl font-medium mb-4"
           
          >
            Manage Your Homestays
          </h2>
          <p className="text-lg text-white/80">
            Access your admin dashboard to manage listings, reviews, and inquiries.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 text-primary mb-12" data-testid="login-logo">
            <Home className="w-8 h-8" />
            <span
              className="text-2xl font-semibold"
             
            >
              Kokan Homestays
            </span>
          </Link>

          {/* Session Expired Warning */}
          {sessionExpired && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Session Expired</p>
                <p className="text-sm text-amber-700">Your session has expired. Please log in again to continue.</p>
              </div>
            </div>
          )}

          {/* Form Header */}
          <div className="mb-8">
            <h1
              className="text-3xl font-semibold text-foreground mb-2"
             
            >
              Admin Login
            </h1>
            <p className="text-muted-foreground">
              Sign in to access your admin dashboard
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Username
              </label>
              <Input
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="h-12 rounded-lg"
                data-testid="login-username-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Password
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-lg pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-primary/90 rounded-full text-lg font-medium"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Back to Home */}
          <div className="mt-8 text-center">
            <Link
              to="/"
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="back-to-home"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
